import React, { useContext } from 'react';
import classnames from 'classnames';
import pathfinder from '@fiverr-private/pathfinder';
import { translate } from '@fiverr-private/i18n-react';
import { useImpressionItem } from '@fiverr-private/impressionable';
import { logWarn } from '../../../utils/logger/logWarn';
import { getTranslatedSubCategoryName } from '../../../utils/catalog/getTranslatedSubCategoryName';
import { MixpanelContext } from '../context/events/MixpanelContext';
import { useBigQuery } from '../BigQuery';
import { useContainerImpressionId } from '../BigQueryImpressionContainer';
import { Action } from '../../../utils/events/card/types';
import { COPIES_PREFIX, DEFAULT_IS_TOUCH, DEFAULT_VARIANT } from './constants';
import { InspireSaveButton } from './InspireSaveButton';
import { InspireCardProps, InspireCardVariant } from './types';
import { OverlayActions } from './OverlayActions';
import { TouchActions } from './TouchActions';

import classes from './InspireCard.module.scss';

export const InspireCard = ({
  inspireDelivery,
  index,
  isTouch = DEFAULT_IS_TOUCH,
  variant = DEFAULT_VARIANT,
  cardHrefRouteOptions,
  onHrefClick,
  onImpression,
}: InspireCardProps) => {
  const id = inspireDelivery.id;
  const originalDeliveryAttachment = inspireDelivery.originalDeliveryAttachment;
  const src = originalDeliveryAttachment.previewUrl.url;
  const gigSlug = inspireDelivery.gig.slug;
  const sellerUserName = inspireDelivery.seller.user.name;
  const subCategoryName = getTranslatedSubCategoryName(inspireDelivery.category.subCategoryId);

  const hrefInspireDeliveryPage = pathfinder('inspire_delivery_page', { id }, cardHrefRouteOptions);

  const bigQuery = useBigQuery();

  const containerImpressionId = useContainerImpressionId();
  const [cardRef, impressionId] = useImpressionItem<HTMLDivElement>({
    sendImpressionToBQ: false, // We're sending the impression event manually.
    intersectionThreshold: 0.25,
    onImpression: (impressionArgs) => {
      onImpression?.(inspireDelivery, index);

      bigQuery.trackImpressionOnCard({
        inspireDelivery,
        index,
        impressionArgs,
      });

      trackMixpanelCardView(id);
    },
  });

  const { trackCardInteraction: trackMixpanelCardInteraction, trackCardView: trackMixpanelCardView } =
    useContext(MixpanelContext);

  const onImageError = () => {
    logWarn('InspireCard', `Could not load an image from CDN. Delivery Id: ${id} | image source: ${src}`, {
      inspireDelivery,
    });
  };

  const sellerDisplayName = translate(`${COPIES_PREFIX}.by_seller_non_clickable`, { params: { sellerUserName } });
  const imgAlt = `${subCategoryName} ${sellerDisplayName}`;

  const isCarouselVariant = variant === InspireCardVariant.carousel;

  const classWithTouch = (baseClass) => classnames(baseClass, { [classes.touch]: isTouch });
  const classWithVariants = (baseClass) =>
    classnames(baseClass, {
      [classes.carouselVariant]: isCarouselVariant,
    });

  const onImageLinkClick = () => {
    if (onHrefClick) {
      onHrefClick(inspireDelivery, index);
    }

    bigQuery.trackClickOnCard({
      inspireDelivery,
      index,
      impressionId,
      containerImpressionId,
      cardElement: 'primary_link',
    });

    trackMixpanelCardInteraction(id, Action.CLICK);
  };

  const onGigLinkClick = () => {
    bigQuery.trackClickOnCard({
      inspireDelivery,
      index,
      impressionId,
      containerImpressionId,
      cardElement: 'gig_link',
    });

    trackMixpanelCardInteraction(id, Action.VIEW_GIG);
  };

  const onSellerLinkClick = () => {
    bigQuery.trackClickOnCard({
      inspireDelivery,
      index,
      impressionId,
      containerImpressionId,
      cardElement: 'seller_link',
    });

    trackMixpanelCardInteraction(id, Action.VIEW_SELLER);
  };

  const onSaved = () => {
    bigQuery.trackClickOnCard({
      inspireDelivery,
      index,
      impressionId,
      containerImpressionId,
      cardElement: 'favorite_button',
    });

    trackMixpanelCardInteraction(id, Action.SAVE);
  };

  const onUnSaved = () => {
    trackMixpanelCardInteraction(id, Action.UN_SAVE);
  };

  return (
    <div ref={cardRef} className={classWithTouch(classes.card)}>
      <div className={classWithVariants(classes.cardMain)}>
        <a href={hrefInspireDeliveryPage} onClick={onImageLinkClick} className={classWithVariants(classes.imageLink)}>
          <img src={src} alt={imgAlt} onError={onImageError} />
        </a>
        {!isTouch && (
          <div className={classWithVariants(classes.hoverOverlay)}>
            <OverlayActions
              gigSlug={gigSlug}
              subCategoryName={subCategoryName}
              sellerUserName={sellerUserName}
              onGigLinkClick={onGigLinkClick}
              onSellerLinkClick={onSellerLinkClick}
            />
          </div>
        )}
        <div className={classWithVariants(classes.heart)}>
          <InspireSaveButton id={id} onSaved={onSaved} onUnSaved={onUnSaved} />
        </div>
      </div>
      {isTouch && (
        <TouchActions
          gigSlug={gigSlug}
          subCategoryName={subCategoryName}
          sellerUserName={sellerUserName}
          onGigLinkClick={onGigLinkClick}
          onSellerLinkClick={onSellerLinkClick}
        />
      )}
    </div>
  );
};

InspireCard.VARIANTS = InspireCardVariant;
